import { rulesetsQuery } from "@common/api/quoting/ruleset"
import { Button } from "@common/components/ui/button"
import { useHasPermission } from "@common/hooks/use-permissions"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { z } from "zod"
import {
  type Display,
  DisplayOptions,
} from "@/components/layout/display-options"
import { FilterBar } from "@/components/layout/filter-bar"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import { CreatorFilter } from "./-components/creator-filter"
import { PlaybookTable } from "./-components/playbook-table"
import {
  GROUPING_LABELS,
  GROUPINGS,
  type Grouping,
  groupingSchema,
} from "./-lib/playbook"

const filterSchema = z.object({
  search: z.string().optional().catch(""),
  creator: z.string().optional().catch(undefined),
  group: groupingSchema.optional().catch(undefined),
  subgroup: groupingSchema.optional().catch(undefined),
})

export const Route = createFileRoute(
  "/_layout/settings/_panel/quoting/playbooks/",
)({
  validateSearch: (search) => filterSchema.parse(search),
  loaderDeps: ({ search }) => ({ search: search.search ?? "" }),
  loader: ({ context: { queryClient }, deps }) =>
    queryClient.ensureQueryData(rulesetsQuery({ search: deps.search })),
  component: PlaybooksPage,
})

function PlaybooksPage(): React.ReactElement {
  const { search = "", creator, group, subgroup } = Route.useSearch()
  const display = {
    group,
    subgroup: group && subgroup !== group ? subgroup : undefined,
  }

  return (
    <div>
      <FilterBar>
        <Toolbar search={search} creator={creator} display={display} />
      </FilterBar>
      <PlaybookTable search={search} creator={creator} display={display} />
    </div>
  )
}

function Toolbar({
  search,
  creator,
  display,
}: {
  search: string
  creator: string | undefined
  display: Display<Grouping>
}): React.ReactElement {
  const navigate = useNavigate()
  const canCreate = useHasPermission("quote_ruleset:create")
  const filter = (next: string) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, search: next || undefined }),
      replace: true,
    })
  const filterByCreator = (next: string | undefined) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, creator: next }),
      replace: true,
    })
  const changeDisplay = (next: Display<Grouping>) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...next }),
      replace: true,
    })

  return (
    <ListToolbar
      actions={
        <>
          <NameFilter
            search={search}
            onSearch={filter}
            placeholder="Filter playbooks…"
          />
          <CreatorFilter
            search={search}
            creator={creator}
            onChange={filterByCreator}
          />
          <DisplayOptions
            groupings={GROUPINGS}
            labels={GROUPING_LABELS}
            {...display}
            onChange={changeDisplay}
          />
          {canCreate && (
            <Button asChild size="sm">
              <Link to="/settings/quoting/playbooks/create">New playbook</Link>
            </Button>
          )}
        </>
      }
    />
  )
}
